import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  checkIsAdmin,
  exportInvestorLeadsCsv,
  getInvestorSummary,
  listInvestorLeads,
  listInvestorOwners,
} from "@/lib/investor-admin.functions";
import { InvestorLeadDetail } from "@/components/admin/InvestorLeadDetail";
import {
  FUNDRAISING_GOAL,
  INVESTOR_STAGES,
  PRESENTATION_GOAL_ANGEL,
  PRESENTATION_GOAL_FAMILY_OFFICE,
  PRESENTATION_GOAL_TOTAL,
  STAGE_LABELS,
  followUpState,
  formatCurrency,
  type InvestorStage,
} from "@/lib/investor-stages";

export const Route = createFileRoute("/_authenticated/admin/investor-leads")({
  head: () => ({
    meta: [
      { title: "Investor Fundraising Workspace · ClutchCube Admin" },
      {
        name: "description",
        content: "Internal ClutchCube investor fundraising pipeline and capital tracking.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: InvestorLeadsPage,
});

type Lead = Record<string, any>;

const DELIVERY_STYLES: Record<string, string> = {
  delivered: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  sent: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  pending: "bg-muted text-muted-foreground border-border",
  bounced: "bg-red-500/15 text-red-400 border-red-500/30",
  complained: "bg-red-500/15 text-red-400 border-red-500/30",
  failed: "bg-red-500/15 text-red-400 border-red-500/30",
  suppressed: "bg-amber-500/15 text-amber-400 border-amber-500/30",
};

const STAGE_STYLES: Partial<Record<InvestorStage, string>> = {
  diligence: "text-violet-400",
  committed: "text-sky-400",
  funded: "text-emerald-400",
  passed: "text-muted-foreground",
  not_qualified: "text-muted-foreground",
};

const SORTS = {
  newest: "Newest",
  oldest: "Oldest",
  score: "Lead Score",
  views: "Deck Views",
  follow_up: "Next Follow-Up",
  estimated: "Estimated Investment",
  committed: "Committed Investment",
  funded: "Funded Investment",
} as const;
type SortKey = keyof typeof SORTS;

function DeliveryCell({ lead }: { lead: Lead }) {
  const state = lead.delivery_state ?? (lead.email_sent ? "sent" : "pending");
  const cls = DELIVERY_STYLES[state] ?? DELIVERY_STYLES["pending"];
  return (
    <div className="min-w-[140px] text-xs">
      <span className={`inline-block rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${cls}`}>
        {state}
      </span>
      {lead.suppressed && (
        <span className="mt-1 block text-[10px] uppercase tracking-[0.15em] text-amber-400">
          Suppressed
        </span>
      )}
      {lead.email_open_count || lead.email_click_count ? (
        <span className="mt-1 block text-muted-foreground">
          {lead.email_open_count ?? 0} opens · {lead.email_click_count ?? 0} clicks
        </span>
      ) : null}
    </div>
  );
}

function Card({ label, value, hint, accent }: { label: string; value: string | number; hint?: string; accent?: string }) {
  return (
    <div className="rounded-sm border border-border bg-card p-4">
      <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
      <p className={`mt-2 font-display text-3xl ${accent ?? ""}`}>{value}</p>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Progress({ pct, className }: { pct: number; className?: string }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={`h-full ${className ?? "bg-primary"}`}
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
      />
    </div>
  );
}

const num = (v: unknown) => Number(v ?? 0) || 0;

function InvestorLeadsPage() {
  const isAdminFn = useServerFn(checkIsAdmin);
  const summaryFn = useServerFn(getInvestorSummary);
  const listFn = useServerFn(listInvestorLeads);
  const ownersFn = useServerFn(listInvestorOwners);
  const exportFn = useServerFn(exportInvestorLeadsCsv);

  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [owners, setOwners] = useState<{ id: string; email: string }[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");
  const [owner, setOwner] = useState("all");
  const [type, setType] = useState("all");
  const [source, setSource] = useState("all");
  const [campaign, setCampaign] = useState("all");
  const [followUp, setFollowUp] = useState("all");
  const [sort, setSort] = useState<SortKey>("newest");

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([summaryFn(), listFn()]);
      setSummary(s);
      setLeads(l.leads as Lead[]);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [summaryFn, listFn]);

  useEffect(() => {
    isAdminFn()
      .then((r) => {
        setIsAdmin(r.isAdmin);
        if (r.isAdmin) {
          load();
          ownersFn()
            .then((o) => setOwners(o.owners))
            .catch(() => setOwners([]));
        }
      })
      .catch((e) => setErr((e as Error).message));
  }, [isAdminFn, load, ownersFn]);

  const ownerEmail = useMemo(
    () => new Map(owners.map((o) => [o.id, o.email])),
    [owners],
  );

  const options = useMemo(() => {
    const uniq = (vals: (string | null | undefined)[]) =>
      Array.from(new Set(vals.filter((v): v is string => !!v))).sort();
    return {
      types: uniq(leads.map((l) => l.investor_type)),
      sources: uniq(leads.map((l) => l.utm_source || l.source)),
      campaigns: uniq(leads.map((l) => l.utm_campaign)),
    };
  }, [leads]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = leads.filter((l) => {
      if (needle) {
        const hay = `${l.name} ${l.email} ${l.company ?? ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (stage !== "all" && l.status !== stage) return false;
      if (owner !== "all" && (owner === "none" ? !!l.owner_id : l.owner_id !== owner)) return false;
      if (type !== "all" && l.investor_type !== type) return false;
      if (source !== "all" && (l.utm_source || l.source) !== source) return false;
      if (campaign !== "all" && l.utm_campaign !== campaign) return false;
      if (followUp !== "all" && followUpState(l.next_follow_up_at) !== followUp) return false;
      return true;
    });
    const time = (v: any) => (v ? new Date(v).getTime() : 0);
    rows = [...rows].sort((a, b) => {
      switch (sort) {
        case "oldest":
          return time(a.created_at) - time(b.created_at);
        case "score":
          return (b.lead_score ?? 0) - (a.lead_score ?? 0);
        case "views":
          return (b.deck_view_count ?? 0) - (a.deck_view_count ?? 0);
        case "follow_up": {
          const av = time(a.next_follow_up_at) || Infinity;
          const bv = time(b.next_follow_up_at) || Infinity;
          return av - bv;
        }
        case "estimated":
          return num(b.estimated_investment) - num(a.estimated_investment);
        case "committed":
          return num(b.committed_investment) - num(a.committed_investment);
        case "funded":
          return num(b.funded_investment) - num(a.funded_investment);
        default:
          return time(b.created_at) - time(a.created_at);
      }
    });
    return rows;
  }, [leads, q, stage, owner, type, source, campaign, followUp, sort]);

  async function onExport() {
    try {
      const { csv, filename } = await exportFn();
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  if (isAdmin === null) {
    return <div className="min-h-screen bg-arena p-10 text-muted-foreground">Loading…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-arena p-10">
        <div className="mx-auto max-w-md rounded-sm border border-border bg-card p-8 text-center">
          <h1 className="mb-2 font-display text-2xl">Not authorized</h1>
          <p className="text-sm text-muted-foreground">Your account does not have admin access.</p>
        </div>
      </div>
    );
  }

  const cap = summary?.capital;
  const pres = summary?.presentations;

  return (
    <div className="min-h-screen bg-arena p-4 sm:p-6 lg:p-10">
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary">ClutchCube · Admin</p>
            <h1 className="font-display text-4xl">Investor Fundraising</h1>
          </div>
          <button
            onClick={onExport}
            className="rounded-sm border border-border px-3 py-2 text-[10px] uppercase tracking-[0.2em] hover:border-primary hover:text-primary"
          >
            Export CSV
          </button>
        </div>

        {err && <p className="mb-4 text-xs text-destructive">{err}</p>}

        {summary && (
          <>
            {/* Capital raise */}
            <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Card label="Fundraising Goal" value={formatCurrency(FUNDRAISING_GOAL)} />
              <Card
                label="Estimated Pipeline"
                value={formatCurrency(cap.estimatedPipeline)}
                hint="Active leads only — not committed capital"
              />
              <Card label="Committed" value={formatCurrency(cap.committed)} accent="text-sky-400" hint={`${cap.committedPct}% of goal`} />
              <Card label="Funded" value={formatCurrency(cap.funded)} accent="text-emerald-400" hint={`${cap.fundedPct}% of goal`} />
            </div>

            <div className="mb-8 rounded-sm border border-border bg-card p-4">
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Progress to $10M
                </p>
                <p className="text-xs text-muted-foreground">
                  <span className="text-emerald-400">{formatCurrency(cap.funded)} funded</span> ·{" "}
                  <span className="text-sky-400">{formatCurrency(cap.committed)} committed</span>
                </p>
              </div>
              <Progress pct={cap.fundedPct} className="bg-emerald-500" />
              <div className="mt-2">
                <Progress pct={cap.committedPct} className="bg-sky-500" />
              </div>
            </div>

            {/* Presentation goal */}
            <div className="mb-8 rounded-sm border border-border bg-card p-4">
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-[10px] uppercase tracking-[0.25em] text-primary">
                  Investor Presentation Goal
                </p>
                <p className="font-display text-2xl">
                  {pres.completed} / {PRESENTATION_GOAL_TOTAL} completed
                </p>
              </div>
              <Progress pct={(pres.completed / PRESENTATION_GOAL_TOTAL) * 100} />
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <p className="text-xs text-muted-foreground">
                  Angel Investors:{" "}
                  <span className="text-foreground">
                    {pres.angels} / {PRESENTATION_GOAL_ANGEL}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Family Offices:{" "}
                  <span className="text-foreground">
                    {pres.familyOffices} / {PRESENTATION_GOAL_FAMILY_OFFICE}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Unclassified completed: <span className="text-foreground">{pres.unclassified}</span>
                </p>
              </div>
            </div>

            {/* Funnel */}
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Card label="Total Leads" value={summary.total} hint={`${summary.excludedTestLeads ?? 0} QA records excluded; retained in the list`} />
              {INVESTOR_STAGES.map((s) => (
                <Card key={s} label={STAGE_LABELS[s]} value={summary.funnel[s]} />
              ))}
            </div>
            <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <Card label="Follow-ups Due Today" value={summary.dueToday} />
              <Card label="Overdue Follow-ups" value={summary.overdue} accent={summary.overdue ? "text-amber-400" : ""} />
              <Card label="Repeat Deck Viewers" value={summary.repeatViewers} />
              <Card label="Avg Deck Views" value={summary.avgViews} />
              <Card label="Avg Lead Score" value={summary.avgLeadScore} />
            </div>
          </>
        )}

        {/* Controls */}
        <div className="mb-3 flex flex-wrap gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, company…"
            className="min-w-[220px] flex-1 rounded-sm border border-border bg-background px-3 py-2 text-sm"
          />
          <select value={stage} onChange={(e) => setStage(e.target.value)} className="rounded-sm border border-border bg-background px-2 py-2 text-xs">
            <option value="all">All stages</option>
            {INVESTOR_STAGES.map((s) => (
              <option key={s} value={s}>{STAGE_LABELS[s]}</option>
            ))}
          </select>
          <select value={owner} onChange={(e) => setOwner(e.target.value)} className="rounded-sm border border-border bg-background px-2 py-2 text-xs">
            <option value="all">All owners</option>
            <option value="none">Unassigned</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>{o.email}</option>
            ))}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-sm border border-border bg-background px-2 py-2 text-xs">
            <option value="all">All types</option>
            {options.types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={source} onChange={(e) => setSource(e.target.value)} className="rounded-sm border border-border bg-background px-2 py-2 text-xs">
            <option value="all">All sources</option>
            {options.sources.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={campaign} onChange={(e) => setCampaign(e.target.value)} className="rounded-sm border border-border bg-background px-2 py-2 text-xs">
            <option value="all">All campaigns</option>
            {options.campaigns.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="rounded-sm border border-border bg-background px-2 py-2 text-xs">
            <option value="all">Any follow-up</option>
            <option value="overdue">Overdue</option>
            <option value="due_today">Due today</option>
            <option value="upcoming">Upcoming</option>
            <option value="none">Not scheduled</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} className="rounded-sm border border-border bg-background px-2 py-2 text-xs">
            {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          {visible.length} of {leads.length} leads
        </p>

        {/* Lead table */}
        <div className="overflow-x-auto rounded-sm border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <tr>
                {["Investor", "Stage", "Owner", "Type", "Source / Campaign", "Delivery", "Views", "Score", "Follow-Up", "Estimated", "Committed", "Funded", "Created"].map((h) => (
                  <th key={h} className="border-b border-border px-3 py-3 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((l) => {
                const fu = followUpState(l.next_follow_up_at);
                return (
                  <tr
                    key={l.id}
                    onClick={() => setOpenId(l.id)}
                    className="cursor-pointer border-b border-border/60 align-top hover:bg-muted/30"
                  >
                    <td className="px-3 py-3">
                      <span className="block whitespace-nowrap">{l.name}</span>
                      <span className="block text-xs text-muted-foreground">{l.email}</span>
                      <span className="block text-xs text-muted-foreground">{l.company || "—"}</span>
                    </td>
                    <td className={`px-3 py-3 whitespace-nowrap text-xs ${STAGE_STYLES[l.status as InvestorStage] ?? ""}`}>
                      {STAGE_LABELS[l.status as InvestorStage] ?? l.status}
                    </td>
                    <td className="px-3 py-3 text-xs">{l.owner_id ? (ownerEmail.get(l.owner_id) ?? "assigned") : "—"}</td>
                    <td className="px-3 py-3 text-xs">{l.investor_type || "—"}</td>
                    <td className="min-w-[150px] px-3 py-3 text-xs">
                      <span className="block">{l.utm_source || l.source}</span>
                      {l.utm_campaign && <span className="block text-muted-foreground">{l.utm_campaign}</span>}
                    </td>
                    <td className="px-3 py-3"><DeliveryCell lead={l} /></td>
                    <td className="px-3 py-3">
                      {l.deck_view_count}
                      {(l.deck_view_count ?? 0) > 1 && (
                        <span className="ml-1 text-[10px] uppercase tracking-[0.15em] text-primary">repeat</span>
                      )}
                    </td>
                    <td className={`px-3 py-3 ${(l.lead_score ?? 0) >= 40 ? "font-semibold text-primary" : ""}`}>
                      {l.lead_score}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs">
                      {fu === "none" ? (
                        <span className="text-muted-foreground">Not scheduled</span>
                      ) : (
                        <span className={fu === "overdue" ? "text-amber-400" : fu === "due_today" ? "text-primary" : ""}>
                          {new Date(l.next_follow_up_at).toLocaleDateString()}
                          {fu === "overdue" && " · overdue"}
                          {fu === "due_today" && " · today"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs">{formatCurrency(l.estimated_investment)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-sky-400">{formatCurrency(l.committed_investment)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-emerald-400">{formatCurrency(l.funded_investment)}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={13} className="px-3 py-10 text-center text-muted-foreground">
                    No investor leads match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Source / campaign performance */}
        {summary?.attribution?.length ? (
          <div className="mt-8 overflow-x-auto rounded-sm border border-border bg-card">
            <p className="border-b border-border px-3 py-3 text-[10px] uppercase tracking-[0.25em] text-primary">
              Source / Campaign Performance
            </p>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr>
                  {["Source", "Campaign", "Leads", "Deck Viewed", "Presentations", "Commitments", "Funded Capital", "Lead→View", "Lead→Pres.", "Lead→Commit"].map((h) => (
                    <th key={h} className="border-b border-border px-3 py-2 text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.attribution.map((a: any) => (
                  <tr key={`${a.source}-${a.campaign}`} className="border-b border-border/60">
                    <td className="px-3 py-2">{a.source}</td>
                    <td className="px-3 py-2">{a.campaign}</td>
                    <td className="px-3 py-2">{a.leads}</td>
                    <td className="px-3 py-2">{a.deckViewed}</td>
                    <td className="px-3 py-2">{a.presentations}</td>
                    <td className="px-3 py-2">{a.commitments}</td>
                    <td className="px-3 py-2 text-emerald-400">{formatCurrency(a.fundedCapital)}</td>
                    <td className="px-3 py-2 text-muted-foreground">{a.leadToDeckView}%</td>
                    <td className="px-3 py-2 text-muted-foreground">{a.leadToPresentation}%</td>
                    <td className="px-3 py-2 text-muted-foreground">{a.leadToCommitment}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {openId && (
        <InvestorLeadDetail
          leadId={openId}
          owners={owners}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
